/**
 * A PARTICLE REACTION, DRAWN AS A FEYNMAN DIAGRAM.
 *
 * ── THE OWNER'S FRAMING, WHICH IS WHAT MAKES THIS SMALL ───────────────────────────────────────
 * *"Would it be possible to write the equation out in the standard editor and then have a
 * 'Feynman' flag that renders the visual?"* Yes, and that is the whole design: the reaction is
 * authored and stored exactly as every other reaction is, and the flag chooses how it is DRAWN.
 * Nothing new is stored but the mediator, because nothing else can be read off the equation.
 *
 * ── WHY NOTHING IS CHECKED ────────────────────────────────────────────────────────────────────
 * A teacher writes WRONG ones on purpose — an incorrect diagram is the distractor in a
 * multiple-choice question, and the student's job is to see what is wrong with it. So charge,
 * lepton number and baryon number are not verified and must not be: a validator here would
 * refuse exactly the diagrams the question needs. Drawing is not endorsement.
 *
 * ── THE MEDIATOR IS ASKED FOR, NOT DERIVED ────────────────────────────────────────────────────
 * `n -> p + e^- + \bar{\nu}` is a W⁻ exchange, but the equation does not say so, and guessing
 * would be both unreliable and the wrong kind of help — picking it is part of what the teacher
 * is teaching. It is also the second thing they will deliberately get wrong.
 *
 * ── TOPOLOGY FOLLOWS THE COUNTS ───────────────────────────────────────────────────────────────
 * One in and two out is a single vertex. One in and three out is a decay: the first vertex emits
 * one product and the mediator, the second emits the rest. Two in and two out is an exchange:
 * two parallel lines with the mediator between them. Those three cover the diagrams a course
 * actually draws, and anything else is declined rather than approximated.
 */

import { splitAtDepthZero } from './reaction-tokens';

export type Mediator = { id: string; label: string; style: 'wave' | 'coil'; speak: string };

export const MEDIATORS: Mediator[] = [
  { id: 'W-', label: 'W⁻', style: 'wave', speak: 'reactions.feynman.wMinus' },
  { id: 'W+', label: 'W⁺', style: 'wave', speak: 'reactions.feynman.wPlus' },
  { id: 'Z0', label: 'Z⁰', style: 'wave', speak: 'reactions.feynman.zZero' },
  { id: 'photon', label: 'γ', style: 'wave', speak: 'reactions.feynman.photon' },
  { id: 'gluon', label: 'g', style: 'coil', speak: 'reactions.feynman.gluon' },
];

export const mediatorById = (id: string) => MEDIATORS.find(m => m.id === id);

export type Topology = 'vertex' | 'decay' | 'exchange';

export type Reading = {
  /** Display forms, left of the arrow and right of it. */
  incoming: string[];
  outgoing: string[];
  topology: Topology;
};

/** Everything that separates one side of the arrow from the other. */
const ARROWS = ['->', '<->', '<=>', '<-->', '→', '↔', '⇌', '⇄', '⟶'];

/**
 * Read a reaction into the two sides of its arrow.
 *
 * Deliberately tolerant about WHICH arrow: a teacher writing a decay reaches for whichever one
 * is nearest, and the diagram does not care. Returns null when there is no arrow, or when the
 * counts are a shape this cannot draw.
 */
export function readReaction(source: string): Reading | null {
  const parts = splitAtDepthZero(source);
  if (!parts) return null;

  const split = parts.findIndex(p => ARROWS.includes(p));
  if (split < 0) return null;

  const side = (list: string[]) => list.filter(p => p !== '+' && p.trim() !== '');
  const incoming = side(parts.slice(0, split));
  const outgoing = side(parts.slice(split + 1));
  if (!incoming.length || !outgoing.length) return null;

  const topology = topologyFor(incoming.length, outgoing.length);
  return topology ? { incoming, outgoing, topology } : null;
}

export function topologyFor(incoming: number, outgoing: number): Topology | null {
  if (incoming === 1 && outgoing === 2) return 'vertex';
  if (incoming === 1 && outgoing === 3) return 'decay';
  if (incoming === 2 && outgoing === 2) return 'exchange';
  return null;
}

// ── Drawing ──────────────────────────────────────────────────────────────────

const W = 260;
const H = 150;

type Point = { x: number; y: number };

/** A fermion line: straight, with an arrowhead at its midpoint saying which way time runs. */
function fermion(a: Point, b: Point): string {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  const size = 5;
  const tip = `${mx + Math.cos(angle) * size},${my + Math.sin(angle) * size}`;
  const left = `${mx + Math.cos(angle + 2.4) * size},${my + Math.sin(angle + 2.4) * size}`;
  const right = `${mx + Math.cos(angle - 2.4) * size},${my + Math.sin(angle - 2.4) * size}`;
  return `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}"/>`
    + `<polygon points="${tip} ${left} ${right}" class="fd-head"/>`;
}

/** A boson line: a wave for W, Z and the photon, a coil for the gluon. */
function boson(a: Point, b: Point, style: 'wave' | 'coil'): string {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  const steps = Math.max(4, Math.round(length / (style === 'coil' ? 11 : 9)));
  const amp = style === 'coil' ? 6 : 5;
  const ux = dx / length;
  const uy = dy / length;
  // The normal, which is what the wave swings along.
  const nx = -uy;
  const ny = ux;

  let d = `M ${a.x} ${a.y}`;
  for (let i = 0; i < steps; i++) {
    const t0 = (i / steps) * length;
    const t1 = ((i + 1) / steps) * length;
    const swing = i % 2 === 0 ? amp : -amp;
    const cx = a.x + ux * ((t0 + t1) / 2) + nx * swing;
    const cy = a.y + uy * ((t0 + t1) / 2) + ny * swing;
    d += ` Q ${cx} ${cy} ${a.x + ux * t1} ${a.y + uy * t1}`;
  }
  return `<path d="${d}" fill="none"/>`;
}

const label = (p: Point, text: string, anchor: 'start' | 'end' | 'middle') =>
  `<text x="${p.x}" y="${p.y}" text-anchor="${anchor}" class="fd-label">${escapeText(text)}</text>`;

const escapeText = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The diagram as SVG, or null when the reaction is not a shape this draws.
 *
 * `particleLabel` turns each side's mhchem into something readable — passed in rather than
 * imported so this module stays free of the display table and can be tested on its own.
 */
export function feynmanSvg(
  reading: Reading,
  mediatorId: string,
  particleLabel: (source: string) => string,
): string | null {
  const mediator = mediatorById(mediatorId);
  if (!mediator) return null;

  const parts: string[] = [];
  const name = (s: string) => particleLabel(s);

  if (reading.topology === 'vertex') {
    /* One vertex: in from the left, two out to the right. */
    const v = { x: W / 2, y: H / 2 };
    parts.push(fermion({ x: 24, y: H / 2 }, v));
    parts.push(label({ x: 18, y: H / 2 + 4 }, name(reading.incoming[0]!), 'end'));
    const outs = [{ x: W - 34, y: 34 }, { x: W - 34, y: H - 34 }];
    reading.outgoing.slice(0, 2).forEach((p, i) => {
      parts.push(fermion(v, outs[i]!));
      parts.push(label({ x: outs[i]!.x + 6, y: outs[i]!.y + 4 }, name(p), 'start'));
    });
  } else if (reading.topology === 'decay') {
    /* Two vertices joined by the mediator: the first emits one product, the second the rest. */
    const v1 = { x: 96, y: 38 };
    const v2 = { x: 150, y: H - 42 };
    parts.push(fermion({ x: 24, y: 38 }, v1));
    parts.push(label({ x: 18, y: 42 }, name(reading.incoming[0]!), 'end'));
    parts.push(fermion(v1, { x: W - 34, y: 38 }));
    parts.push(label({ x: W - 28, y: 42 }, name(reading.outgoing[0]!), 'start'));
    parts.push(boson(v1, v2, mediator.style));
    parts.push(label({ x: v1.x - 8, y: (v1.y + v2.y) / 2 }, mediator.label, 'end'));
    const outs = [{ x: W - 34, y: H - 62 }, { x: W - 34, y: H - 18 }];
    reading.outgoing.slice(1, 3).forEach((p, i) => {
      parts.push(fermion(v2, outs[i]!));
      parts.push(label({ x: outs[i]!.x + 6, y: outs[i]!.y + 4 }, name(p), 'start'));
    });
  } else {
    /* Exchange: two lines passing, the mediator between them. */
    const v1 = { x: W / 2, y: 40 };
    const v2 = { x: W / 2, y: H - 40 };
    parts.push(fermion({ x: 28, y: 40 }, v1));
    parts.push(fermion(v1, { x: W - 38, y: 40 }));
    parts.push(fermion({ x: 28, y: H - 40 }, v2));
    parts.push(fermion(v2, { x: W - 38, y: H - 40 }));
    parts.push(label({ x: 22, y: 44 }, name(reading.incoming[0]!), 'end'));
    parts.push(label({ x: 22, y: H - 36 }, name(reading.incoming[1]!), 'end'));
    parts.push(label({ x: W - 32, y: 44 }, name(reading.outgoing[0]!), 'start'));
    parts.push(label({ x: W - 32, y: H - 36 }, name(reading.outgoing[1]!), 'start'));
    parts.push(boson(v1, v2, mediator.style));
    parts.push(label({ x: W / 2 - 10, y: H / 2 + 4 }, mediator.label, 'end'));
  }

  const dots = [
    reading.topology === 'vertex' ? [{ x: W / 2, y: H / 2 }] : [],
    reading.topology === 'decay' ? [{ x: 96, y: 38 }, { x: 150, y: H - 42 }] : [],
    reading.topology === 'exchange' ? [{ x: W / 2, y: 40 }, { x: W / 2, y: H - 40 }] : [],
  ].flat().map(p => `<circle cx="${p.x}" cy="${p.y}" r="3" class="fd-vertex"/>`).join('');

  return `<svg viewBox="0 0 ${W} ${H}" class="fd" role="img" aria-hidden="true"`
    + ` stroke="currentColor" stroke-width="1.4" fill="none">${parts.join('')}${dots}</svg>`;
}

// ── Saying it, because the drawing is hidden from a reader ───────────────────

/**
 * The two letters whose meaning the diagram settles.
 *
 * Read as chemistry, `n` is nitrogen and `p` is phosphorus, and the reaction speech says "N" and
 * "P" because that is right everywhere else. In a Feynman diagram they are the neutron and the
 * proton, and "Feynman diagram, N in" is simply wrong. The flag is what makes the context
 * unambiguous, so the override lives here and nowhere else.
 */
const PARTICLE_SPEECH: Record<string, string> = {
  n: 'reactions.symbols.neutron',
  p: 'reactions.symbols.proton',
};

/**
 * What a screen reader is handed INSTEAD of the diagram.
 *
 * The SVG is `aria-hidden`, so this is the whole of what a blind student gets. It leads with
 * what the thing IS, because a bare particle list is indistinguishable from the equation — and
 * the equation is on the page in the other half of the question.
 *
 * It describes and does not judge. The diagram may be deliberately wrong, and saying so would
 * hand a blind student the answer the sighted one has to work out — the same rule the marks
 * follow, where a highlight is "highlighted" and never why.
 */
export function feynmanSpeech(
  reading: Reading,
  mediatorId: string,
  t: (key: string) => string,
  sayParticle: (source: string) => string,
): string | null {
  const mediator = mediatorById(mediatorId);
  if (!mediator) return null;
  const list = (items: string[]) =>
    items.map(p => (PARTICLE_SPEECH[p] ? t(PARTICLE_SPEECH[p]!) : sayParticle(p))).join(', ');
  return [
    t('reactions.feynman.label'),
    `${list(reading.incoming)} ${t('reactions.feynman.in')}`,
    `${t(mediator.speak)} ${t('reactions.feynman.exchanged')}`,
    `${list(reading.outgoing)} ${t('reactions.feynman.out')}`,
  ].join(', ');
}
